// src/plugins/builder/generate.ts
// Template engine: takes a template id + user config and produces a working
// plugin (manifest + pluginTsx + hooksTs + README). Also wires the new plugin
// into the in-browser dynamic registry so the admin panel picks it up.
//
// No fabrication — every path is exercised by plugins.builder.test.ts.
import { BuilderError } from './types';
import { TEMPLATES } from './templates';
import { saveUserPlugin, getUserPlugin, deleteUserPlugin, listUserPluginsSync } from './storage';
import { notifyDynamicChange } from './dynamicRegistry';
// ----- lookups --------------------------------------------------------------
export function getTemplate(templateId) {
    return TEMPLATES.find(t => t.id === templateId) ?? null;
}
export function listTemplates() {
    return TEMPLATES.slice();
}
// ----- id helpers -----------------------------------------------------------
/**
 * Build an instance id from the template + a small suffix. User plugins are
 * namespaced with `user.` so they never collide with bundled plugin ids.
 */
export function newInstanceId(templateId) {
    const suffix = Math.random().toString(36).slice(2, 8);
    return `user.${templateId}.${suffix}`;
}
export function isUserInstanceId(id) {
    return id.startsWith('user.');
}
// ----- core generate --------------------------------------------------------
/** Pure: run the template's generate() with the supplied config. */
export function generateFiles(template, params) {
    validateConfigAgainstSchema(template, params.config);
    const out = template.generate(params);
    // Sanity-check what the template produced — every returned manifest must
    // match the instance id so loader lookups succeed.
    if (out.manifest.id !== params.instanceId) {
        throw new BuilderError(`Template "${template.id}" produced a manifest id "${out.manifest.id}" that doesn't match the instance id "${params.instanceId}".`);
    }
    if (!out.manifestJson || !out.manifestJson.trim().startsWith('{')) {
        throw new BuilderError('The plugin file we were about to save looked broken.');
    }
    return out;
}
/** Validate that the user's filled-in config satisfies the template schema. */
function validateConfigAgainstSchema(template, config) {
    const schema = template.configSchema;
    const required = schema.required ?? [];
    for (const key of required) {
        const v = config[key];
        if (v === undefined || v === null || v === '') {
            const title = schema.properties[key]?.title ?? key;
            throw new BuilderError(`Please fill in "${title}" — it's needed.`);
        }
    }
    // Type-level validation for number ranges + enum coverage.
    for (const [key, prop] of Object.entries(schema.properties)) {
        const v = config[key];
        if (v === undefined || v === null || v === '')
            continue;
        if (prop.type === 'number') {
            const n = typeof v === 'number' ? v : Number(v);
            if (Number.isNaN(n)) {
                throw new BuilderError(`"${prop.title ?? key}" needs a number.`);
            }
            if (typeof prop.minimum === 'number' && n < prop.minimum) {
                throw new BuilderError(`"${prop.title ?? key}" can't be less than ${prop.minimum}.`);
            }
            if (typeof prop.maximum === 'number' && n > prop.maximum) {
                throw new BuilderError(`"${prop.title ?? key}" can't be more than ${prop.maximum}.`);
            }
        }
        if (prop.enum && prop.enum.length && typeof v === 'string' && !prop.enum.includes(v)) {
            throw new BuilderError(`"${prop.title ?? key}" needs to be one of: ${prop.enum.join(', ')}.`);
        }
        if (prop.format === 'email' && typeof v === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
            throw new BuilderError(`"${prop.title ?? key}" doesn't look like an email address.`);
        }
        if (prop.format === 'url' && typeof v === 'string' && !/^https?:\/\//i.test(v)) {
            throw new BuilderError(`"${prop.title ?? key}" needs to start with http:// or https://`);
        }
    }
}
// ----- install / edit / delete ---------------------------------------------
/**
 * Build & save a user plugin, then wake the registry so the admin page
 * sees it immediately. Returns the saved record.
 */
export async function installFromWizard(params) {
    const template = getTemplate(params.templateId);
    if (!template) {
        throw new BuilderError(`The template "${params.templateId}" isn't available.`);
    }
    const instanceId = params.instanceId ?? newInstanceId(template.id);
    const generated = generateFiles(template, {
        instanceId,
        instanceName: params.instanceName,
        config: params.config,
    });
    const now = new Date().toISOString();
    const existing = params.instanceId ? await getUserPlugin(params.instanceId) : null;
    const record = {
        instanceId,
        templateId: template.id,
        name: params.instanceName,
        config: { ...params.config },
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
        generated,
    };
    const saved = await saveUserPlugin(record);
    notifyDynamicChange();
    return saved;
}
/** Duplicate an existing user plugin under a new id + "(copy)" suffix. */
export async function duplicateUserPlugin(instanceId) {
    const source = await getUserPlugin(instanceId);
    if (!source)
        return null;
    const newName = source.name.endsWith('(copy)') ? source.name : `${source.name} (copy)`;
    return installFromWizard({
        templateId: source.templateId,
        instanceName: newName,
        config: source.config,
    });
}
/** Remove a user plugin. */
export async function removeUserPlugin(instanceId) {
    await deleteUserPlugin(instanceId);
    notifyDynamicChange();
}
// ----- convenience ----------------------------------------------------------
/** Sync snapshot — used by the registry adapter to build entries in one pass. */
export function currentUserPluginsSync() {
    return listUserPluginsSync();
}
